// ============================================================================
// DASHBOARD QUICK CARDS HOOKS - SUMMARY DATA FOR CAROUSEL
// ----------------------------------------------------------------------------
// Each card needs only an AGGREGATE (a sum + a count), never the underlying
// rows. These hooks call Postgres aggregate RPCs (SECURITY INVOKER, so RLS
// applies exactly as the prior Apollo reads did) via supabase-js `.rpc()`,
// wrapped in TanStack Query. This replaced the old pattern of fetching
// `first: 100` FULL rows and summing them in the browser — which was wasteful
// (heavy row payloads pulled only to total them) AND silently wrong past 100
// rows (the communications inbound/outbound split capped at 100). pg_graphql
// exposes no aggregates, hence the RPCs.
//
// Migration: supabase/migrations/20260629140000_dashboard_summary_rpcs.sql
// PostgREST returns numeric/bigint as JSON; values are coerced with Number()
// defensively (no BigFloat string handling needed on the PostgREST path).
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { useUniversalAuth, useAuth } from "@west-tennessee-consulting/ui";
import { monthKey, previousMonthKey } from "@west-tennessee-consulting/recurring-revenue";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { ACTIVE_STAGES } from "@/lib/constants/deal-stages";
import {
  computeMrrMovement,
  type MrrMovement,
  type MrrSnapshotRow,
} from "@/lib/dashboard/metrics/mrr-movement";
import type {
  CommunicationsQuickCardData,
  DealsQuickCardData,
  RecurringTransactionsQuickCardData,
  RecurringChargesQuickCardData,
  ArAgingQuickCardData,
  ArAgingTileData,
  ArAgingBucketSummary,
  AgingBucket,
  QuickCardsData,
} from "@/lib/types/dashboard-responses";

const num = (v: number | string | null | undefined): number => Number(v ?? 0) || 0;

// ============================================================================
// COMMUNICATIONS QUICK CARD HOOK
// ============================================================================

export function useCommunicationsQuickCard(): CommunicationsQuickCardData {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const enabled = isAuthenticated && !!appUser;

  // Last 7 days. Memoized to a stable ISO so the query key doesn't churn each render.
  const sevenDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }, []);

  const q = useQuery({
    queryKey: ["dashboard", "communications-summary", sevenDaysAgo],
    enabled,
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase.rpc("dashboard_communications_summary", {
        p_since: sevenDaysAgo,
      });
      if (error) throw new Error(error.message);
      return data?.[0] ?? null;
    },
  });

  return useMemo(
    () => ({
      totalCount: num(q.data?.total_count),
      inboundCount: num(q.data?.inbound_count),
      outboundCount: num(q.data?.outbound_count),
      isLoading: q.isLoading,
      error: q.error ?? null,
      refetch: () => {
        void q.refetch();
      },
    }),
    [q.data, q.isLoading, q.error, q.refetch]
  );
}

// ============================================================================
// DEALS QUICK CARD HOOK
// ============================================================================

export function useDealsQuickCard(): DealsQuickCardData {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const { isClientUser } = useRoleCheck();
  // Clients don't see deals — skip the query entirely.
  const enabled = isAuthenticated && !!appUser && !isClientUser;

  // ACTIVE_STAGES (lib/constants/deal-stages.ts) stays the single source of
  // truth for "open pipeline"; it's passed to the RPC, never duplicated in SQL.
  const activeStages = useMemo(() => [...ACTIVE_STAGES] as string[], []);

  const q = useQuery({
    queryKey: ["dashboard", "deals-summary", activeStages],
    enabled,
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase.rpc("dashboard_deals_summary", {
        p_active_stages: activeStages,
      });
      if (error) throw new Error(error.message);
      return data?.[0] ?? null;
    },
  });

  return useMemo(
    () => ({
      totalPipelineValue: num(q.data?.total_pipeline_value),
      openDealsCount: num(q.data?.open_deals_count),
      currency: "USD",
      isLoading: q.isLoading,
      error: q.error ?? null,
      refetch: () => {
        void q.refetch();
      },
    }),
    [q.data, q.isLoading, q.error, q.refetch]
  );
}

// ============================================================================
// RECURRING TRANSACTIONS QUICK CARD HOOK
// ============================================================================

export function useRecurringTransactionsQuickCard(): RecurringTransactionsQuickCardData {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const enabled = isAuthenticated && !!appUser;

  const q = useQuery({
    queryKey: ["dashboard", "recurring-transactions-summary"],
    enabled,
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase.rpc("dashboard_recurring_transactions_summary");
      if (error) throw new Error(error.message);
      return data?.[0] ?? null;
    },
  });

  return useMemo(
    () => ({
      totalMonthlyAmount: num(q.data?.total_monthly_amount),
      pendingSyncsCount: num(q.data?.pending_syncs_count),
      currency: "USD",
      isLoading: q.isLoading,
      error: q.error ?? null,
      refetch: () => {
        void q.refetch();
      },
    }),
    [q.data, q.isLoading, q.error, q.refetch]
  );
}

// ============================================================================
// RECURRING CHARGES QUICK CARD HOOK
// ============================================================================

export function useRecurringChargesQuickCard(): RecurringChargesQuickCardData {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const enabled = isAuthenticated && !!appUser;

  const q = useQuery({
    queryKey: ["dashboard", "recurring-charges-summary"],
    enabled,
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase.rpc("dashboard_recurring_charges_summary");
      if (error) throw new Error(error.message);
      return data?.[0] ?? null;
    },
  });

  return useMemo(
    () => ({
      // RPC returns CENTS (metadata.amount is stored in cents) — convert once.
      totalMonthlyCharges: num(q.data?.total_cents) / 100,
      nextChargeDate: q.data?.next_charge_date ?? null,
      currency: "USD",
      isLoading: q.isLoading,
      error: q.error ?? null,
      refetch: () => {
        void q.refetch();
      },
    }),
    [q.data, q.isLoading, q.error, q.refetch]
  );
}

// ============================================================================
// AR AGING QUICK CARD HOOK (admin-only)
// ----------------------------------------------------------------------------
// Overdue-receivables aging for the owner/CEO. Reuses the AR-aging cron's
// aggregation via GET /api/dashboard/ar-aging (server holds QuickBooks tokens,
// so this fetches with a bearer token rather than an RPC). The upstream QBO
// scan is heavy, so we only enable it for admins and keep the result fresh for
// 30 minutes.
// ============================================================================

const AR_AGING_EMPTY_BUCKETS: Record<AgingBucket, ArAgingBucketSummary> = {
  "1-30": { count: 0, amount: 0 },
  "31-60": { count: 0, amount: 0 },
  "61-90": { count: 0, amount: 0 },
  "90+": { count: 0, amount: 0 },
};

export function useArAgingQuickCard(): ArAgingQuickCardData {
  const { isAuthenticated, isAdmin } = useUniversalAuth();
  const { getAuthToken } = useAuth();
  const enabled = isAuthenticated && isAdmin;

  const q = useQuery({
    queryKey: ["dashboard", "ar-aging"],
    enabled,
    staleTime: 30 * 60 * 1000, // 30 min — upstream QuickBooks scan is heavy
    queryFn: async (): Promise<ArAgingTileData | null> => {
      const token = await getAuthToken();
      const res = await fetch("/api/dashboard/ar-aging", {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to load AR aging");
      const json = (await res.json()) as { data?: ArAgingTileData };
      return json?.data ?? null;
    },
  });

  return useMemo(
    () => ({
      totalOverdueBalance: q.data?.totalOverdueBalance ?? 0,
      overdueCount: q.data?.overdueCount ?? 0,
      buckets: q.data?.buckets ?? AR_AGING_EMPTY_BUCKETS,
      currency: "USD",
      isLoading: q.isLoading,
      error: q.error ?? null,
      refetch: () => {
        void q.refetch();
      },
    }),
    [q.data, q.isLoading, q.error, q.refetch]
  );
}

// ============================================================================
// MRR MOVEMENT HOOK (admin-only)
// ----------------------------------------------------------------------------
// Month-over-month MRR movement (new/expansion/contraction/churn) for the
// recurring-transactions MRR shown on the quick card. Diffs the current vs prior
// month rows in mrr_snapshots (admin-read RLS). Returns null until at least one
// prior month has been captured (movement needs two months of history).
// ============================================================================

export function useMrrMovement(): { movement: MrrMovement | null; isLoading: boolean } {
  const { isAuthenticated, isAdmin } = useUniversalAuth();
  const enabled = isAuthenticated && isAdmin;

  const q = useQuery({
    queryKey: ["dashboard", "mrr-movement"],
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour — snapshots only change monthly
    queryFn: async (): Promise<MrrMovement | null> => {
      const supabase = getBrowserSupabaseClient();
      // Both keys come from `@wtc/recurring-revenue`, the same module the
      // mrr-snapshot cron writes them with. The prior month is derived from the
      // KEY, not by `Date.setMonth(-1)` on an instant — that rolls over on the
      // 31st (Mar 31 - 1 month = Mar 3), so on eight days a year the hook was
      // diffing against the wrong baseline.
      const current = monthKey(new Date());
      const prior = previousMonthKey(current);

      const { data, error } = await supabase
        .from("mrr_snapshots")
        .select("source, account_id, amount, month")
        .eq("source", "recurring_transaction")
        .in("month", [current, prior]);
      if (error) throw new Error(error.message);

      const toRow = (r: { source: string; account_id: string; amount: number | string | null }): MrrSnapshotRow => ({
        source: r.source,
        account_id: r.account_id,
        amount: typeof r.amount === "number" ? r.amount : Number(r.amount ?? 0),
      });

      const rows = data ?? [];
      const priorRows = rows.filter((r) => r.month === prior).map(toRow);
      const currentRows = rows.filter((r) => r.month === current).map(toRow);
      // No prior-month history yet → no meaningful movement to show.
      if (priorRows.length === 0) return null;
      return computeMrrMovement(priorRows, currentRows);
    },
  });

  return { movement: q.data ?? null, isLoading: q.isLoading };
}

// ============================================================================
// COMBINED QUICK CARDS DATA HOOK
// ============================================================================

export function useQuickCardsData(): QuickCardsData {
  const communications = useCommunicationsQuickCard();
  const deals = useDealsQuickCard();
  const recurringTransactions = useRecurringTransactionsQuickCard();
  const recurringCharges = useRecurringChargesQuickCard();

  return useMemo(
    () => ({
      communications,
      deals,
      recurringTransactions,
      recurringCharges,
      isLoading:
        communications.isLoading ||
        deals.isLoading ||
        recurringTransactions.isLoading ||
        recurringCharges.isLoading,
      error:
        communications.error ||
        deals.error ||
        recurringTransactions.error ||
        recurringCharges.error,
    }),
    [communications, deals, recurringTransactions, recurringCharges]
  );
}
